import { useCallback, useEffect, useMemo, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';

import { QUERY_DEFAULTS, TAG_FQN_PREFIXES } from '@/shared/lib/constants';
import { assetKeys, discoveryKeys } from '@/shared/lib/query/queryKeys';
import type { TierEditHook } from '@/shared/types/editHooks';

import type { AssetBaseInfo } from '@/features/asset-detail/api';
import { fetchAvailableTiers, generateTierPatchOperation, updateAssetTier } from '@/features/asset-detail/api/tiersApi';

type UseAssetTierEditProps = {
  assetId: string;
  entityType: string;
  fqn: string;
};

export const useAssetTierEdit = ({ assetId, entityType, fqn }: UseAssetTierEditProps): TierEditHook => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTierFQN, setSelectedTierFQN] = useState<string | null>(null);

  const assetData = queryClient.getQueryData<AssetBaseInfo>(assetKeys.assetBaseInfo(entityType, fqn));
  const currentTier = assetData?.tags?.find((t) => t.tagFQN.startsWith(TAG_FQN_PREFIXES.TIER));
  const currentTierFQN = currentTier?.tagFQN || null;

  const { data: availableTiers = [], isLoading: isLoadingTiers } = useQuery({
    queryKey: assetKeys.availableTiers(),
    queryFn: fetchAvailableTiers,
    staleTime: QUERY_DEFAULTS.STALE_TIME.LONG,
  });

  const { mutate: updateTier, isPending: isUpdating } = useMutation({
    mutationFn: async ({ newTierFQN }: { newTierFQN: string | null }) => {
      const previousData = queryClient.getQueryData<AssetBaseInfo>(assetKeys.assetBaseInfo(entityType, fqn));
      const currentTags = previousData?.tags || [];

      const patchOperations = generateTierPatchOperation(currentTags, newTierFQN, availableTiers);

      if (patchOperations.length === 0) {
        return;
      }

      await updateAssetTier(entityType, assetId, patchOperations);
    },
    onMutate: async ({ newTierFQN }) => {
      await queryClient.cancelQueries({
        queryKey: assetKeys.assetBaseInfo(entityType, fqn),
      });

      const previousData = queryClient.getQueryData<AssetBaseInfo>(assetKeys.assetBaseInfo(entityType, fqn));

      const tierInfo = availableTiers.find((tier) => tier.fullyQualifiedName === newTierFQN);

      queryClient.setQueryData<AssetBaseInfo>(assetKeys.assetBaseInfo(entityType, fqn), (old) => {
        if (!old) return old;

        if (newTierFQN === null) {
          return {
            ...old,
            tier: undefined,
          };
        }

        return {
          ...old,
          tier: {
            tagFQN: newTierFQN,
            name: tierInfo?.name || newTierFQN,
          },
        };
      });

      return { previousData };
    },
    onError: (error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(assetKeys.assetBaseInfo(entityType, fqn), context.previousData);
      }

      const errorMessage = error instanceof Error ? error.message : '중요 등급 업데이트 중 오류가 발생했습니다.';

      notification.error({
        message: '중요 등급 업데이트 실패',
        description: errorMessage,
        placement: 'bottomLeft',
        duration: 5,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: discoveryKeys.searchPrefix() });
      notification.success({
        message: '중요 등급 업데이트 완료',
        description: '중요 등급이 성공적으로 업데이트되었습니다.',
        placement: 'bottomLeft',
        duration: 3,
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: assetKeys.assetBaseInfo(entityType, fqn),
      });
    },
  });

  useEffect(() => {
    setSelectedTierFQN(currentTierFQN);
  }, [currentTierFQN]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    const latestAssetData = queryClient.getQueryData<AssetBaseInfo>(assetKeys.assetBaseInfo(entityType, fqn));
    const latestTier = latestAssetData?.tags?.find((t) => t.tagFQN.startsWith(TAG_FQN_PREFIXES.TIER));
    if (latestTier) {
      setSelectedTierFQN(latestTier.tagFQN);
    } else {
      setSelectedTierFQN(null);
    }
  }, [queryClient, entityType, fqn]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    const latestAssetData = queryClient.getQueryData<AssetBaseInfo>(assetKeys.assetBaseInfo(entityType, fqn));
    const latestTier = latestAssetData?.tags?.find((t) => t.tagFQN.startsWith(TAG_FQN_PREFIXES.TIER));
    if (latestTier) {
      setSelectedTierFQN(latestTier.tagFQN);
    } else {
      setSelectedTierFQN(null);
    }
  }, [queryClient, entityType, fqn]);

  const handleSelect = useCallback((tierFQN: string) => {
    setSelectedTierFQN((prev) => (prev === tierFQN ? null : tierFQN));
  }, []);

  const handleUpdate = useCallback(
    (tierFQN: string) => {
      updateTier({ newTierFQN: tierFQN });
      setIsOpen(false);
    },
    [updateTier]
  );

  const hasChanges = useMemo(() => {
    return currentTierFQN !== selectedTierFQN;
  }, [currentTierFQN, selectedTierFQN]);

  return {
    isOpen,
    selectedTierFQN,
    availableTiers,
    isLoadingTiers,
    isUpdating,
    hasChanges,
    handleOpen,
    handleClose,
    handleSelect,
    handleUpdate,
  };
};
